import {useUserStore} from '@/store/user'
import {useCartStore} from '@/store/cart'
import {storeToRefs} from 'pinia'
import {useHydraulicSelectorStore} from '~/store/hydraulic-selector'
import {
  ADDITEM_STATUS,
  HYDRAULIC_MESSAGE_KEYS,
  HYDRAULIC_SELECTOR_SEALS_INPUTS,
  PROPERTY_SCOPE_NAMES,
  REGION_KEYS
} from '~/assets/js/constants'
import {usePandaService} from '~/composables/panda_service'
import {useProductService} from '~/composables/product_service'
import useI18nExtended from '~/composables/usei18nExtended'
import {newEmptyPostBody} from '@/types/helper.d'
import {useGtm} from '@gtm-support/vue-gtm'
import {useHydraulicSelectorNAService} from '~/composables/hydraulic_selector_na_service'
import {useEventItemConverter} from '@/composables/useEventItemConverter'
import type {EventItemInput} from '@/types/event-item-converter'

export const useHydraulicHelper = () => {
  const app = useNuxtApp()
  const i18n = useI18nExtended()

  const hydraulicSelectorStore = useHydraulicSelectorStore()
  const {loggedIn, excludedBrands} = storeToRefs(useUserStore())
  const {addItems} = useCartStore()
  const {getAvailabilitiesBulk} = usePandaService()
  const {getProductsBySkusNew} = useProductService()

  const currentResult: Ref<any> = ref({})
  const currentSketchResult: Ref<any> = ref([])
  const currentAvailabilities: Ref<any> = ref([])
  const isLoading: Ref<boolean> = ref(false)

  const loading = computed(() => {
    return isLoading.value
  })

  const handleAddAll = async (seals: any) => {
    let allSeals: any[]
    allSeals = []
    allSeals = allSeals.concat(seals.wipers)
    allSeals = allSeals.concat(seals.rodSeals)
    allSeals = allSeals.concat(seals.pistonSeals)
    allSeals = allSeals.concat(seals.bufferSeals)
    allSeals = allSeals.concat(seals.rodGuides)
    allSeals = allSeals.concat(seals.pistonGuides)
    const sealsToAdd = allSeals.map(s => ({sku: s?.sku, quantity: 1}))
    const res = await addItems({itemsToAdd: sealsToAdd, region: app.$globalization.getRegion(), locale: i18n.locale, item_list_id: 'seals', item_list_name: 'sealsAll'})
    if (res && res.length > 0 && res[0].status === ADDITEM_STATUS.SUCCESS) {
      app.$toast.success(i18n.tv('successBulk', 'hydraulicSelector'))
    } else {
      app.$toast.error(i18n.tv('failed', 'cart'))
      return
    }
    return
  }

  const suggestedSeals = computed(() => {
    const result = {
      wipers: currentResult.value.wipers?.products?.filter(p => p.recommendationRank === 1).map((p) => ({...p, designSketch: currentSketchResult.value?.find(s => s?.sku === p?.sku)?.designSketchUri, availability: currentAvailabilities.value?.find(a => a?.sku === p?.sku)})),
      rodSeals: currentResult.value.rodSeals?.products?.filter(p => p.recommendationRank === 1).map((p) => ({...p, designSketch: currentSketchResult.value?.find(s => s?.sku === p?.sku)?.designSketchUri, availability: currentAvailabilities.value?.find(a => a?.sku === p?.sku)})),
      pistonSeals: currentResult.value.pistonSeals?.products?.filter(p => p.recommendationRank === 1).map((p) => ({...p, designSketch: currentSketchResult.value?.find(s => s?.sku === p?.sku)?.designSketchUri, availability: currentAvailabilities.value?.find(a => a?.sku === p?.sku)})),
      bufferSeals: currentResult.value.bufferSeals?.products?.filter(p => p.recommendationRank === 1).map((p) => ({...p, designSketch: currentSketchResult.value?.find(s => s?.sku === p?.sku)?.designSketchUri, availability: currentAvailabilities.value?.find(a => a?.sku === p?.sku)})),
      rodGuides: currentResult.value.rodGuides?.products?.filter(p => p.recommendationRank === 1).map((p) => ({...p, designSketch: currentSketchResult.value?.find(s => s?.sku === p?.sku)?.designSketchUri, availability: currentAvailabilities.value?.find(a => a?.sku === p?.sku)})),
      pistonGuides: currentResult.value.pistonGuides?.products?.filter(p => p.recommendationRank === 1).map((p) => ({...p, designSketch: currentSketchResult.value?.find(s => s?.sku === p?.sku)?.designSketchUri, availability: currentAvailabilities.value?.find(a => a?.sku === p?.sku)}))
    }
    if (currentResult.value.bufferSeals?.messages?.includes(HYDRAULIC_MESSAGE_KEYS.BufferMessage)) {
      result.bufferSeals.push({message: HYDRAULIC_MESSAGE_KEYS.BufferMessage})
    }
    return result
  })

  const alternativeSeals = computed(() => {
    const result = {
      wipers: currentResult.value.wipers?.products?.filter(p => p.recommendationRank !== 1).map((p) => ({...p, designSketch: currentSketchResult.value?.find(s => s?.sku === p?.sku)?.designSketchUri, availability: currentAvailabilities.value?.find(a => a?.sku === p?.sku)})),
      rodSeals: currentResult.value.rodSeals?.products?.filter(p => p.recommendationRank !== 1).map((p) => ({...p, designSketch: currentSketchResult.value?.find(s => s?.sku === p?.sku)?.designSketchUri, availability: currentAvailabilities.value?.find(a => a?.sku === p?.sku)})),
      pistonSeals: currentResult.value.pistonSeals?.products?.filter(p => p.recommendationRank !== 1).map((p) => ({...p, designSketch: currentSketchResult.value?.find(s => s?.sku === p?.sku)?.designSketchUri, availability: currentAvailabilities.value?.find(a => a?.sku === p?.sku)})),
      bufferSeals: currentResult.value.bufferSeals?.products?.filter(p => p.recommendationRank !== 1).map((p) => ({...p, designSketch: currentSketchResult.value?.find(s => s?.sku === p?.sku)?.designSketchUri, availability: currentAvailabilities.value?.find(a => a?.sku === p?.sku)})),
      rodGuides: currentResult.value.rodGuides?.products?.filter(p => p.recommendationRank !== 1).map((p) => ({...p, designSketch: currentSketchResult.value?.find(s => s?.sku === p?.sku)?.designSketchUri, availability: currentAvailabilities.value?.find(a => a?.sku === p?.sku)})),
      pistonGuides: currentResult.value.pistonGuides?.products?.filter(p => p.recommendationRank !== 1).map((p) => ({...p, designSketch: currentSketchResult.value?.find(s => s?.sku === p?.sku)?.designSketchUri, availability: currentAvailabilities.value?.find(a => a?.sku === p?.sku)}))
    }
    if (currentResult.value.bufferSeals?.messages?.includes(HYDRAULIC_MESSAGE_KEYS.BufferMessage)) {
      result.bufferSeals.push({message: HYDRAULIC_MESSAGE_KEYS.BufferMessage})
    }
    return result
  })

  const skuList = computed(() => {
    let skuList = []
    skuList = skuList.concat(currentResult.value.wipers?.products?.map(p => p?.sku))
    skuList = skuList.concat(currentResult.value.rodSeals?.products?.map(p => p?.sku))
    skuList = skuList.concat(currentResult.value.rodGuides?.products?.map(p => p?.sku))
    skuList = skuList.concat(currentResult.value.bufferSeals?.products?.map(p => p?.sku))
    skuList = skuList.concat(currentResult.value.pistonSeals?.products?.map(p => p?.sku))
    skuList = skuList.concat(currentResult.value.pistonGuides?.products?.map(p => p?.sku))
    return [...new Set(skuList)]
  })

  const skuListSuggested = computed(() => {
    let skuList = []
    skuList = skuList.concat(suggestedSeals.value.wipers?.map(p => p?.sku))
    skuList = skuList.concat(suggestedSeals.value.rodSeals?.map(p => p?.sku))
    skuList = skuList.concat(suggestedSeals.value.rodGuides?.map(p => p?.sku))
    skuList = skuList.concat(suggestedSeals.value.bufferSeals?.map(p => p?.sku))
    skuList = skuList.concat(suggestedSeals.value.pistonSeals?.map(p => p?.sku))
    skuList = skuList.concat(suggestedSeals.value.pistonGuides?.map(p => p?.sku))
    return [...new Set(skuList.filter(s => s !== undefined))]
  })

  const isExcluded = computed(() => {
    const brandOfProduct = 'Freudenberg'
    return typeof brandOfProduct === 'string' ? (excludedBrands.value.some(s => brandOfProduct.toLowerCase().startsWith(s.toLowerCase())) ? true : false ) : false
  })

  const skuListAlternative = computed(() => {
    let skuList = []
    skuList = skuList.concat(alternativeSeals.value.wipers?.map(p => p?.sku))
    skuList = skuList.concat(alternativeSeals.value.rodSeals?.map(p => p?.sku))
    skuList = skuList.concat(alternativeSeals.value.rodGuides?.map(p => p?.sku))
    skuList = skuList.concat(alternativeSeals.value.bufferSeals?.map(p => p?.sku))
    skuList = skuList.concat(alternativeSeals.value.pistonSeals?.map(p => p?.sku))
    skuList = skuList.concat(alternativeSeals.value.pistonGuides?.map(p => p?.sku))
    return [...new Set(skuList.filter(s => s !== undefined))]
  })

  const getCheckedCheckboxes = (checkboxes) => {
    return Object.keys(checkboxes).filter(key => checkboxes[key] === true).length
  }

  const getResultsForNewConstruction = (results) => {
    let total = 6
    let countObjectsWithProducts = 0

    Object.keys(results).forEach(key => {
      const innerObject = results[key]
      if (innerObject?.products?.length > 0) {
        countObjectsWithProducts++
      }
      if (key === 'bufferSeals' && innerObject.messages?.includes(HYDRAULIC_MESSAGE_KEYS.BufferMessage)) {
        total = total - 1
      }
    })

    return (countObjectsWithProducts / total) * 100
  }

  const showNoResultsInfoNew = computed(() => {
    return getResultsForNewConstruction(currentResult.value) === 0
  })

  const showPartlyResultsInfoNew = computed(() => {
    return getResultsForNewConstruction(currentResult.value) > 0 && getResultsForNewConstruction(currentResult.value) < 100
  })

  const showNoResultsInfoRep = computed(() => {
    return getResultsForReplacement(currentResult.value, hydraulicSelectorStore.checkboxes) === 0
  })

  const showPartlyResultsInfoRep = computed(() => {
    return getResultsForReplacement(currentResult.value, hydraulicSelectorStore.checkboxes) > 0 && getResultsForReplacement(currentResult.value, hydraulicSelectorStore.checkboxes) < 100
  })

  const getResultsForReplacement = (results, checkboxes) => {
    let count = 0
    let totalCheckboxes = Object.keys(checkboxes).filter(key => checkboxes[key] === true).length
    for (const key in checkboxes) {
      const resultKey = key + 's'
      if (checkboxes[key] && results[resultKey] && results[resultKey].products && results[resultKey].products.length > 0) {
        count += 1
      }
      if (checkboxes[key] && key === 'bufferSeal' && results?.bufferSeals?.messages?.includes(HYDRAULIC_MESSAGE_KEYS.BufferMessage)) {
        totalCheckboxes = totalCheckboxes - 1
      }
    }

    if (totalCheckboxes > 0) {
      return (count / totalCheckboxes) * 100
    }
    return 0
  }

  const findSeals = async (shouldTrack: boolean = true) => {
    isLoading.value = true
    hydraulicSelectorStore.isErrorResponse = false
    currentResult.value = app.$globalization.getRegion() !== REGION_KEYS.NA
      ? await useHydraulicSelectorService().postHydraulicSelectorValues(hydraulicSelectorStore.getPostRequestObject) ?? {}
      : await useHydraulicSelectorNAService().postHydraulicSelectorValues(hydraulicSelectorStore.getPostRequestObject) ?? {}
    if (Object.keys(currentResult.value).length === 0 && currentResult.value.constructor === Object) {
      hydraulicSelectorStore.isErrorResponse = true
      isLoading.value = false
      return
    }
    hydraulicSelectorStore.wiperProfile = currentResult.value.wipers?.products?.find(p => p.recommendationRank === 1)?.design
    hydraulicSelectorStore.rodSealProfile = currentResult.value.rodSeals?.products?.find(p => p.recommendationRank === 1)?.design
    hydraulicSelectorStore.rodGuideProfile = currentResult.value.rodGuides?.products?.find(p => p.recommendationRank === 1)?.design
    hydraulicSelectorStore.bufferSealProfile = currentResult.value.bufferSeals?.messages?.includes(HYDRAULIC_MESSAGE_KEYS.BufferMessage) ? 'EMPTYBUFFERSEAL' : currentResult.value.bufferSeals?.products?.find(p => p.recommendationRank === 1)?.design
    hydraulicSelectorStore.pistonSealProfile = currentResult.value.pistonSeals?.products?.find(p => p.recommendationRank === 1)?.design
    hydraulicSelectorStore.pistonGuideProfile = currentResult.value.pistonGuides?.products?.find(p => p.recommendationRank === 1)?.design
    currentSketchResult.value = await getProductsBySkusNew(skuList.value, app.$globalization.getRegion(), i18n.locale.value, PROPERTY_SCOPE_NAMES.HYDRAULIC_SELECTOR_NEW, null, true)

    isLoading.value = false
    if (loggedIn.value && !isExcluded.value) {
      currentAvailabilities.value = await getAvailabilitiesBulk(skuList.value.map(sku => ({sku, quantity: 1})))
    }

    const checkboxes = hydraulicSelectorStore.checkboxes
    const checkedCheckboxes = getCheckedCheckboxes(checkboxes)
    const resultsForNewConstruction = getResultsForNewConstruction(currentResult.value)
    const resultsForReplacement = getResultsForReplacement(currentResult.value, checkboxes)

    if (shouldTrack) {
      const options: EventItemInput = {
        event: 'hydras_show_products',
        category: 'hydraulic-selector',
        action: 'search_seals',
        add_personalization: true,
        props: {
          useCase: hydraulicSelectorStore.basicInput.sealsNeeds,
          rodDiameter: hydraulicSelectorStore.basicInput.rodDiameter,
          outerDiameter: hydraulicSelectorStore.basicInput?.outerDiameter,
          applicationArea: hydraulicSelectorStore.basicInput.applicationArea,
          expectedResults: hydraulicSelectorStore.basicInput.sealsNeeds === 'newCylinderDesign' ? 6 : checkedCheckboxes,
          returnedResultsOnPercentage: hydraulicSelectorStore.basicInput.sealsNeeds === 'newCylinderDesign' ? resultsForNewConstruction : resultsForReplacement,
          wipers_result: currentResult.value.wipers?.products?.length ?? 0,
          rodGuides_result: currentResult.value.rodGuides?.products?.length ?? 0,
          rodSeals_result: currentResult.value.rodSeals?.products?.length ?? 0,
          bufferSeals_result: currentResult.value.bufferSeals?.products?.length ?? 0,
          pistonSeals_result: currentResult.value.pistonSeals?.products?.length ?? 0,
          pistonGuides_result: currentResult.value.pistonGuides?.products?.length ?? 0
        },
        items: []
      }

      const ga4payload = useEventItemConverter().convert(options)
      useGtm()?.trackEvent(ga4payload)
    }
  }

  const reset = () => {
    hydraulicSelectorStore.setCurrentData(newEmptyPostBody())
    hydraulicSelectorStore.basicInput = {
      rodDiameter: null,
      outerDiameter: null,
      sealsNeeds: 'newCylinderDesign',
      applicationArea: hydraulicSelectorStore.presets.sort((a, b) => a.displayOrder - b.displayOrder)[0]?.applicationPreset
    }
    hydraulicSelectorStore.checkboxes = {}
    hydraulicSelectorStore.wiperDefault = 'design_ab'
    hydraulicSelectorStore.pistonSealDefaultB = false
    hydraulicSelectorStore.isErrorResponse = false
    hydraulicSelectorStore.initializeUnits()
    navigateTo(app.$globalization.localePath('/hydraulic-selector/'))
  }

  const fieldsPerColumn = (column: string) => {
    const el =
      HYDRAULIC_SELECTOR_SEALS_INPUTS[
        column as keyof typeof HYDRAULIC_SELECTOR_SEALS_INPUTS
      ]
    if (el?.items) {
      if (
        el.complexInstallationSpace &&
        Array.isArray(el.complexInstallationSpace)
      ) {
        return [...el.items, ...el.complexInstallationSpace]
      } else {
        return [...el.items]
      }
    }
    return []
  }

  const currentUnitLengthLabel = computed(() => {
    return i18n.tv(hydraulicSelectorStore.isInch ? 'inch' : 'mm', 'unitsOfMeasure')
  })

  const currentUnitTemperatureLabel = computed(() => {
    return i18n.tv(hydraulicSelectorStore.unitTemperature, 'unitsOfMeasure')
  })

  const metadataForLength = computed(() => {
    return hydraulicSelectorStore.isInch
      ? hydraulicSelectorStore.metadataImperial
      : hydraulicSelectorStore.metadata
  })

  const changePressureUnit = (unit: string) => {
    hydraulicSelectorStore.setUnitPressure(unit)
    const pressureMaxPreset = hydraulicSelectorStore.unitPressure === 'bar'
      ? hydraulicSelectorStore.presets?.find((preset) => preset.applicationPreset === 'agriculture-machinery')?.pressureMax
      : hydraulicSelectorStore.presetsImperial?.find((preset) => preset.applicationPreset === 'agriculture-machinery')?.pressureMax
    const pressureConstPreset = hydraulicSelectorStore.unitPressure === 'bar'
      ? hydraulicSelectorStore.presets?.find((preset) => preset.applicationPreset === 'agriculture-machinery')?.pressureConst
      : hydraulicSelectorStore.presetsImperial?.find((preset) => preset.applicationPreset === 'agriculture-machinery')?.pressureConst

    hydraulicSelectorStore.currentData.pressureMax = pressureMaxPreset
    hydraulicSelectorStore.currentData.pressureConst = pressureConstPreset
  }

  const changeSpeedUnit = (unit: string) => {
    hydraulicSelectorStore.setUnitSpeed(unit)
    hydraulicSelectorStore.currentData.speedMax = hydraulicSelectorStore.unitSpeed === 'm/s'
      ? hydraulicSelectorStore.presets?.find((preset) => preset.applicationPreset === 'agriculture-machinery')?.speedMax
      : hydraulicSelectorStore.presetsImperial?.find((preset) => preset.applicationPreset === 'agriculture-machinery')?.speedMax
  }

  const changeTemperatureUnit = (unit: string) => {
    hydraulicSelectorStore.setUnitTemperature(unit)
    const temperatureMinPreset = hydraulicSelectorStore.unitTemperature === 'celsius'
      ? hydraulicSelectorStore.presets?.find((preset) => preset.applicationPreset === 'agriculture-machinery')?.temperatureMin
      : hydraulicSelectorStore.presetsImperial?.find((preset) => preset.applicationPreset === 'agriculture-machinery')?.temperatureMin
    const temperatureMaxPreset = hydraulicSelectorStore.unitTemperature === 'celsius'
      ? hydraulicSelectorStore.presets?.find((preset) => preset.applicationPreset === 'agriculture-machinery')?.temperatureMax
      : hydraulicSelectorStore.presetsImperial?.find((preset) => preset.applicationPreset === 'agriculture-machinery')?.temperatureMax

    hydraulicSelectorStore.currentData.temperatureMin = temperatureMinPreset
    hydraulicSelectorStore.currentData.temperatureMax = temperatureMaxPreset
  }

  const roundNumber = (number: number, decimalPoints: number = 2) => {
    return parseFloat(number.toFixed(decimalPoints))
  }

  const performUnitLengthSwitch = (unit: string, rodDiameter: number|null|undefined, cylinderDiameter: number|null|undefined) => {
    hydraulicSelectorStore.setUnitLength(unit)
    updateBasicInput(rodDiameter, cylinderDiameter)
    convertCurrentDataObjects(unit)
  }

  const updateBasicInput = (rodDiameter: number|null|undefined, cylinderDiameter: number|null|undefined) => {
    hydraulicSelectorStore.basicInput = {
      rodDiameter: rodDiameter,
      outerDiameter: cylinderDiameter,
      applicationArea: hydraulicSelectorStore.basicInput.applicationArea,
      sealsNeeds: hydraulicSelectorStore.basicInput.sealsNeeds,
      applicationAreaUnknown: hydraulicSelectorStore.basicInput.applicationAreaUnknown
    }
  }

  const convertCurrentDataObjects = (unit: string) => {
    const keysToConvert = ['wiper', 'rodSeal', 'rodGuide', 'pistonSeal', 'pistonGuide', 'bufferSeal']
    keysToConvert.forEach((key) => {
      if (hydraulicSelectorStore.currentData[key]) {
        convertCurrentDataObject(unit, key)
      }
    })
  }

  const convertCurrentDataObject = (unit: string, key: string) => {
    Object.keys(hydraulicSelectorStore.currentData[key]).forEach((nestedKey) => {
      let value = hydraulicSelectorStore.currentData[key][nestedKey]
      if (value && typeof value === 'number') {
        hydraulicSelectorStore.currentData[key][nestedKey] = convertValue(unit, value)
      }
    })
  }

  const convertValue = (unit: string, value: any) => {
    if (!value) {
      return null
    }
    if (unit === 'inch') {
      return roundNumber(value / 25.4, 3)
    }

    return roundNumber(value * 25.4)
  }

  return {
    suggestedSeals,
    alternativeSeals,
    handleAddAll,
    findSeals,
    reset,
    skuListSuggested,
    skuListAlternative,
    loggedIn,
    isExcluded,
    fieldsPerColumn,
    showNoResultsInfoNew,
    showPartlyResultsInfoNew,
    showNoResultsInfoRep,
    showPartlyResultsInfoRep,
    loading,
    currentUnitLengthLabel,
    currentUnitTemperatureLabel,
    metadataForLength,
    changePressureUnit,
    changeTemperatureUnit,
    changeSpeedUnit,
    roundNumber,
    updateBasicInput,
    convertValue,
    convertCurrentDataObjects,
    performUnitLengthSwitch
  }
}
